import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Footer from "../components/footer";
import GenerateSidebar from "../components/generatesidebar";
import Header from "../components/header";
import useWindowSize from "../services/useWindowSize";
import { useSanctum } from "react-sanctum";
import Toaster from "../components/toaster";
import { platformEvents } from "../services/eventbus/platformEvents";
import { HotspotSpec } from "../services/useMarzipano";
import { Api } from "../services/Api";
import SaveGeneration from "../components/save-generation";
import Popup from "../components/popup";

export interface StyleInterface {
  id: string;
  name: string;
  image_url: string;
  max_characters: number;
}

export interface GenerationInterface {
  id: string;
  image_url: string;
  positive_prompt: string;
  negative_prompt: string | null;
  style_id: string;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface GenerationPageProps {
  generation: GenerationInterface | null;
  requestedGeneration: (generation: GenerationInterface) => void;
  scene: any | null;
  saveGenerationPopupOpen: boolean;
  setSaveGenerationPopupOpen: (open: boolean) => void;
}

interface SceneSpec {
  isCurrent: boolean;
  key: string;
  hotspots: Map<string, HotspotSpec>;
  imageUrl: string;
  movieUrl: string;
  type: string;
}

const GenerateLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toasterRef = useRef<any>();
  const { user, checkAuthentication, authenticated } = useSanctum();
  const windowSize = useWindowSize();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [generation, setGeneration] = useState<GenerationInterface | null>(null);
  const [previousGenerations, setPreviousGenerations] = useState<GenerationInterface[]>([]);
  const [scene, setScene] = useState<Map<string, SceneSpec> | null>(null);
  const [saveGenerationPopupOpen, setSaveGenerationPopupOpen] = useState<boolean>(false);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => windowSize.width && windowSize.width < 1280 && setSidebarOpen(false);

  const pollGeneration = async (generationId: string) => {
    try {
      const response = await Api(`/users/content/generate/${generationId}`);
      if (response.image_url || response.error_message) {
        setGeneration(response);
        onGenerationClick(response);
        setPreviousGenerations(prev => prev.map(gen => gen.id === generationId ? response : gen));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error polling generation:', error);
      return true;
    }
  };

  useEffect(() => {
    if (!authenticated) {
      checkAuthentication().then(auth => !auth && navigate("/login?p=" + encodeURIComponent(location.pathname)));
    }
  }, [authenticated, checkAuthentication, location.pathname, navigate]);

  useEffect(() => {
    const handleSuccess = (message: string) => toasterRef.current?.addToast("Success", message, "success");
    const handleFailure = (message: string) => toasterRef.current?.addToast("Failed", message, "danger");

    const unsubscribeSuccess = platformEvents.on("generalSuccess", handleSuccess);
    const unsubscribeFailed = platformEvents.on("generalFailure", handleFailure);

    return () => {
      unsubscribeSuccess();
      unsubscribeFailed();
    };
  }, []);

  useEffect(() => {
    if (authenticated) {
      Api("/users/content/generate").then(setPreviousGenerations);
    }
  }, [authenticated]);

  useEffect(() => {
    if (generation && !generation.image_url && !generation.error_message) {
      const interval = setInterval(async () => {
        const shouldStop = await pollGeneration(generation.id);
        if (shouldStop) clearInterval(interval);
      }, 5000);
      return () => clearInterval(interval);
    } else if (!generation) {
      setScene(new Map([
        ["1", {
          isCurrent: true,
          key: "1", 
          hotspots: new Map<string, HotspotSpec>([]),
          imageUrl: "",
          movieUrl: "",
          type: "equirect",
        }],
      ]));
    }
  }, [generation]);

  useEffect(() => {
    setSidebarOpen(windowSize.width ? windowSize.width > 1280 : false);
  }, [windowSize]);

  if (!user) return null;

  const requestedGeneration = (generation: GenerationInterface) => {
    setPreviousGenerations(prev => [generation, ...prev]);
    onGenerationClick(generation);
  };

  const onGenerationClick = async (generation: GenerationInterface) => {
    setGeneration(generation);
    if (generation.image_url) {
      setGeneration(prev => prev ? { ...prev, error_message: null } : null);
      try {
        const response = await fetch(generation.image_url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        setScene(new Map([
          ["1", {
            isCurrent: true,
            key: "1", 
            hotspots: new Map<string, HotspotSpec>([]),
            imageUrl: generation.image_url,
            movieUrl: "",
            type: "equirect",
          }],
        ]));
      } catch (error: any) {
        setGeneration(prev => prev ? { ...prev, error_message: 'Failed to load the image for this generation. Please try again later.' } : null);
        setScene(new Map([
          ["1", {
            isCurrent: true,
            key: "1", 
            hotspots: new Map<string, HotspotSpec>([]),
            imageUrl: "",
            movieUrl: "",
            type: "equirect",
          }],
        ]));
      }
    }
  };

  return (
    <>
      {sidebarOpen && !location.pathname.includes("/library/hotspots") && (
        <GenerateSidebar 
          outsideClicked={closeSidebar} 
          selectedGeneration={generation} 
          previousGenerations={previousGenerations} 
          onGenerationClick={onGenerationClick} 
        />
      )}
      <div className="custom-scrollbar flex flex-1 flex-col overflow-y-auto">
        {!location.pathname.includes("/library/hotspots") && (
          <Header user={user} menuButtonClicked={toggleSidebar} />
        )}
        <Outlet context={{ generation, requestedGeneration, scene, saveGenerationPopupOpen, setSaveGenerationPopupOpen } as GenerationPageProps} />
        {!location.pathname.includes("/library/hotspots") && <Footer />}
      </div>
      <Toaster ref={toasterRef} timeout={5000} />
      {generation && saveGenerationPopupOpen && (
        <Popup>
          <SaveGeneration
            generation={generation}
            onClose={() => setSaveGenerationPopupOpen(false)}
            onSave={() => {
              setSaveGenerationPopupOpen(false);
              setGeneration(null);
              setPreviousGenerations(prev => prev.filter(gen => gen.id !== generation.id));
            }}
          />
        </Popup>
      )}
    </>
  );
};

export default GenerateLayout;
